import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import { useEffect, useState } from "react";
import { Shield, Terminal, Music, BarChart3, Check, ArrowRight, Ticket, Coins, Radio, Sparkles, Gamepad2, DoorOpen, Zap, MousePointerClick, Settings2 } from "lucide-react";
import { BrandLogo } from "@/components/BrandLogo";
import { ThemeToggle } from "@/components/ThemeToggle";
import api from "@/lib/api";

const features = [
  { icon: Shield, title: "Moderation & Auto-Mod", desc: "Anti-spam, word filters, raid protection, cases and full audit logs." },
  { icon: Ticket, title: "Tickets & Applications", desc: "Support panels, transcripts, staff roles and recruitment forms." },
  { icon: Sparkles, title: "AI Help & Studio", desc: "An AI assistant that answers members, reviews code and creates images." },
  { icon: Coins, title: "Leveling & Economy", desc: "XP, role rewards, leaderboards and a full server economy." },
  { icon: Radio, title: "Live & Social Alerts", desc: "Twitch, YouTube and social posts announced the moment they go live." },
  { icon: DoorOpen, title: "Welcome & Roles", desc: "Welcome cards, auto-roles, reaction roles and verification." },
  { icon: Terminal, title: "Custom Commands", desc: "Build commands, timers and sticky messages without writing code." },
  { icon: Music, title: "Music & Radio", desc: "Queue songs, DJ controls and live web radio in voice channels." },
  { icon: Gamepad2, title: "Fun & Games", desc: "Jokes, memes and a library of browser games, right from Discord." },
];

const steps = [
  { icon: MousePointerClick, title: "Sign in with Discord", desc: "We only list servers you can manage." },
  { icon: Zap, title: "Invite the bot", desc: "One click adds Crank.live with the right permissions." },
  { icon: Settings2, title: "Switch on modules", desc: "Configure everything from the dashboard. Changes apply instantly." },
];

function HeroPreview() {
  const rows = [["Moderation", true], ["Tickets", true], ["AI Help", true], ["Leveling", false]];
  return (
    <div className="relative rounded-2xl border border-zinc-800 bg-zinc-900 shadow-2xl overflow-hidden" aria-hidden="true">
      <div className="flex items-center gap-1.5 px-4 h-10 border-b border-zinc-800 bg-zinc-950">
        <span className="w-2.5 h-2.5 rounded-full bg-red-400/70" /><span className="w-2.5 h-2.5 rounded-full bg-amber-400/70" /><span className="w-2.5 h-2.5 rounded-full bg-emerald-400/70" />
        <span className="ml-3 text-[11px] text-zinc-500 font-mono">crank.live/app</span>
      </div>
      <div className="grid grid-cols-[120px_1fr] min-h-[280px]">
        <div className="border-r border-zinc-800 p-3 space-y-1.5">
          {["Overview", "Moderation", "Tickets", "AI Help", "Social", "Economy"].map((l, i) => (
            <div key={l} className={`text-[11px] rounded-md px-2 py-1.5 ${i === 1 ? "bg-volt/10 text-volt font-medium" : "text-zinc-500"}`}>{l}</div>
          ))}
        </div>
        <div className="p-4 space-y-3">
          <div className="grid grid-cols-3 gap-2">
            {[["Members", "12.4k"], ["Messages", "48k"], ["Cases", "31"]].map(([k, v]) => (
              <div key={k} className="rounded-lg border border-zinc-800 bg-zinc-950 p-2.5"><div className="text-[9px] uppercase tracking-wider text-zinc-500">{k}</div><div className="font-head font-bold">{v}</div></div>
            ))}
          </div>
          {rows.map(([l, on]) => (
            <div key={l} className="flex items-center justify-between rounded-lg border border-zinc-800 bg-zinc-950 px-3 py-2.5">
              <span className="text-xs font-medium">{l}</span>
              <span className={`w-8 h-4 rounded-full relative ${on ? "bg-volt" : "bg-zinc-700"}`}><span className={`absolute top-0.5 w-3 h-3 rounded-full bg-[#fff] ${on ? "right-0.5" : "left-0.5"}`} /></span>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}

function PlanCard({ id, p }) {
  const hot = id === "pro";
  return (
    <div data-testid={`plan-card-${id}`} className={`relative rounded-2xl p-7 border bg-zinc-900 flex flex-col ${hot ? "border-volt glow-volt" : "border-zinc-800"}`}>
      {hot && <div className="absolute -top-3 left-7 text-[11px] font-bold uppercase tracking-wider bg-volt text-volt-ink px-3 py-1 rounded-full">Most popular</div>}
      <div className="text-xs font-bold uppercase tracking-[0.2em] text-zinc-500">{p.name}</div>
      <div className="mt-3 font-head text-4xl font-black">£{p.price}<span className="text-base font-normal text-zinc-500">/mo</span></div>
      <ul className="mt-6 space-y-3 flex-1">
        {p.features.map((feat) => (
          <li key={feat} className="flex items-start gap-2 text-sm text-zinc-300"><Check className="w-4 h-4 text-volt mt-0.5 shrink-0" /> {feat}</li>
        ))}
      </ul>
      <Link to="/register" className={`mt-8 block text-center rounded-lg px-5 py-2.5 font-semibold transition-colors ${hot ? "bg-volt text-volt-ink hover:bg-volt-hover" : "bg-zinc-800 hover:bg-zinc-700"}`}>
        {id === "free" ? "Start free" : "Choose " + p.name}
      </Link>
    </div>
  );
}

export default function Landing() {
  const [plans, setPlans] = useState({});
  useEffect(() => { api.get("/plans").then((r) => setPlans(r.data)).catch(() => {}); }, []);

  return (
    <div className="min-h-screen bg-black text-zinc-50">
      <header className="sticky top-0 z-50 backdrop-blur-xl bg-black/70 border-b border-zinc-800">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 h-16 flex items-center justify-between">
          <div className="flex items-center gap-2 font-head font-black text-xl"><BrandLogo className="w-7 h-7" /> crank<span className="text-volt">.live</span></div>
          <nav className="flex items-center gap-2 sm:gap-3">
            <a href="#features" className="hidden md:block text-sm text-zinc-400 hover:text-white px-3 py-2">Features</a>
            <a href="#pricing" className="hidden md:block text-sm text-zinc-400 hover:text-white px-3 py-2">Pricing</a>
            <ThemeToggle />
            <Link to="/login" data-testid="nav-login-btn" className="text-sm text-zinc-300 hover:text-white px-3 py-2">Log in</Link>
            <Link to="/register" data-testid="nav-signup-btn" className="text-sm bg-volt text-volt-ink font-semibold rounded-lg px-4 sm:px-5 py-2 hover:bg-volt-hover transition-colors">Add to Discord</Link>
          </nav>
        </div>
      </header>

      <section className="relative overflow-hidden">
        <div className="absolute inset-0 grid-bg pointer-events-none" />
        <div className="absolute inset-0 hero-glow pointer-events-none" />
        <div className="max-w-7xl mx-auto px-4 sm:px-6 pt-16 sm:pt-24 pb-16 sm:pb-24 grid lg:grid-cols-[1.1fr_1fr] gap-12 items-center relative">
          <motion.div initial={{ opacity: 0, y: 16 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.5 }}>
            <div className="inline-flex items-center gap-2 text-xs font-bold uppercase tracking-[0.2em] text-volt border border-volt/30 bg-volt/5 rounded-full px-4 py-1.5 mb-7">
              <span className="w-1.5 h-1.5 bg-volt rounded-full live-dot" /> One bot. Every module.
            </div>
            <h1 className="font-head text-4xl sm:text-5xl lg:text-6xl font-black tracking-tight leading-[1.02]">
              Run your whole <span className="text-volt">Discord</span> server from one dashboard.
            </h1>
            <p className="mt-6 text-base sm:text-lg text-zinc-400 max-w-xl">
              Moderation, tickets, AI help, live alerts, leveling and more. Switch modules on with one click and Crank.live handles the rest.
            </p>
            <div className="mt-9 flex flex-wrap items-center gap-3">
              <Link to="/register" data-testid="hero-cta-btn" className="group bg-volt text-volt-ink font-semibold rounded-lg px-7 py-3.5 hover:bg-volt-hover transition-colors flex items-center gap-2 glow-volt">
                Get started free <ArrowRight className="w-4 h-4 group-hover:translate-x-1 transition-transform" />
              </Link>
              <Link to="/login" data-testid="hero-dashboard-btn" className="border border-zinc-700 rounded-lg px-7 py-3.5 hover:bg-zinc-900 transition-colors font-medium">Open dashboard</Link>
            </div>
          </motion.div>
          <motion.div initial={{ opacity: 0, y: 24 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.6, delay: 0.1 }}>
            <HeroPreview />
          </motion.div>
        </div>
      </section>

      <section id="features" className="max-w-7xl mx-auto px-4 sm:px-6 py-16 sm:py-24">
        <div className="max-w-2xl mb-12">
          <div className="text-xs font-semibold uppercase tracking-[0.18em] text-volt mb-3">Modules</div>
          <h2 className="font-head text-3xl sm:text-4xl font-bold tracking-tight">Everything your community needs</h2>
          <p className="text-zinc-400 mt-3">Each module can be switched on, configured and fine-tuned per server.</p>
        </div>
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
          {features.map((f, i) => (
            <motion.div key={f.title} initial={{ opacity: 0, y: 16 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }} transition={{ delay: i * 0.04 }}
              className="bg-zinc-900 border border-zinc-800 rounded-2xl p-6 hover:border-volt/40 transition-colors">
              <div className="w-11 h-11 rounded-xl bg-volt/10 flex items-center justify-center mb-4"><f.icon className="w-5 h-5 text-volt" /></div>
              <h3 className="font-head text-lg font-semibold mb-1.5">{f.title}</h3>
              <p className="text-zinc-400 text-sm leading-relaxed">{f.desc}</p>
            </motion.div>
          ))}
        </div>
      </section>

      <section className="border-y border-zinc-800 bg-zinc-950">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 py-16 sm:py-20 grid md:grid-cols-3 gap-8">
          {steps.map((s, i) => (
            <div key={s.title} className="flex gap-4">
              <div className="font-head text-4xl font-black text-zinc-800">0{i + 1}</div>
              <div>
                <div className="flex items-center gap-2 font-head font-semibold text-lg"><s.icon className="w-4 h-4 text-volt" /> {s.title}</div>
                <p className="text-sm text-zinc-400 mt-1">{s.desc}</p>
              </div>
            </div>
          ))}
        </div>
      </section>

      <section id="pricing" className="max-w-7xl mx-auto px-4 sm:px-6 py-16 sm:py-24">
        <div className="max-w-2xl mb-12">
          <div className="text-xs font-semibold uppercase tracking-[0.18em] text-volt mb-3">Pricing</div>
          <h2 className="font-head text-3xl sm:text-4xl font-bold tracking-tight">Start free. Upgrade per server.</h2>
        </div>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-5">
          {Object.entries(plans).map(([id, p]) => <PlanCard key={id} id={id} p={p} />)}
        </div>
      </section>

      <section className="max-w-7xl mx-auto px-4 sm:px-6 pb-20">
        <div className="relative overflow-hidden rounded-3xl border border-volt/30 bg-volt/5 px-6 sm:px-12 py-12 flex flex-col md:flex-row items-start md:items-center justify-between gap-6">
          <div>
            <h2 className="font-head text-2xl sm:text-3xl font-bold tracking-tight">Ready to power up your server?</h2>
            <p className="text-zinc-400 mt-2">Set up in under a minute. No credit card needed.</p>
          </div>
          <Link to="/register" data-testid="cta-band-btn" className="bg-volt text-volt-ink font-semibold rounded-lg px-7 py-3.5 hover:bg-volt-hover transition-colors flex items-center gap-2 shrink-0">Add to Discord <ArrowRight className="w-4 h-4" /></Link>
        </div>
      </section>

      <footer className="border-t border-zinc-800">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 py-10 flex flex-col sm:flex-row items-center justify-between gap-4 text-sm text-zinc-500">
          <div className="flex items-center gap-2 font-head font-black text-white"><BrandLogo className="w-6 h-6" /> crank.live</div>
          <p>© 2026 Crank.live — Not affiliated with Discord Inc.</p>
        </div>
      </footer>
    </div>
  );
}
